import { GenericItemApiResponse, GenericListApiResponse, PagedListApiResponse } from 'src/app/core/_interface/apiResponse';
import { Order, OrderItem, OrderItemStatus, OrderItemToCancel, OrderStatusChangeItem, PlacedOrder, SearchOrderOptions } from 'src/app/core/_interface/order';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { accessDeniedDescriptions } from '../_helpers/accessDeniedDescriptions';
import { ApiResponse } from '../_interface/apiResponse';
import { DeliveryType, OrderDeliveryDetail, OrderStatus } from '../_interface/order';
import { NGXLogger } from 'ngx-logger';

@Injectable({
  providedIn: 'root'
})
export class OrderService {

  constructor(public http: HttpClient,
    private logger: NGXLogger
  ) { }

  setStatus(orderID: number,
    status: OrderStatus,
    group?: string,
    preparationTime?: number,
    dueDate?: Date,
    rejectReason?: string): Observable<boolean> {

    this.logger.debug('OrderService.setStatus', orderID, status, group, preparationTime, dueDate, rejectReason);

    const url = environment.apiBase + `order/SetStatus`;
    var data = {
      orderID: orderID,
      status: status,
      group: group,
      preparationTime: preparationTime,
      dueDate: dueDate,
      rejectReason
    };

    return this.http.post<GenericItemApiResponse<string>>(url, data,
      { headers: { accessDeniedDescription: accessDeniedDescriptions.changeOrderStatus } })
      .pipe(
        map(response => {
          if (response.success) {
            return response.success
          }

          throw response;
        })
      );
  }

  /**
   * Predaje delivery porudžbinu dostavljaču (Order.WaiterId = driverUserID) i prebacuje je u status
   * Dispached. Konobar time gubi zaduženje za porudžbinu. Dostavljač je zaposleni iste firme.
   */
  assignDriver(orderID: number, driverUserID: number): Observable<boolean> {
    this.logger.debug('OrderService.assignDriver', orderID, driverUserID);

    const url = environment.apiBase + `order/AssignDriver`;
    const data = { orderID, driverUserID };

    return this.http.post<ApiResponse>(url, data)
      .pipe(
        map(response => {
          if (response.success) {
            return true;
          }
          throw response;
        })
      );
  }

  setOrderItemStatus(item: OrderItem | null, status: OrderItemStatus, ids?: number[]) {
    this.logger.debug('OrderService.setOrderItemStatus', item, status, ids);

    const url = environment.apiBase + `order/SetOrderItemStatus`;
    var data = {
      orderItemID: item?.orderItemID,
      status,
      ids: ids
    };

    return this.http.post<ApiResponse>(url, data)
      .pipe(
        map(response => {
          if (response.success) {
            return response.success
          }

          throw response;
        })
      );
  }

  /**
   * @param replacesOrderID Kiosk "izmeni porudžbinu": nova porudžbina menja postojeću
   * neplaćenu PendingPayment porudžbinu — server kanceluje staru i prenosi njen
   * tracking token/vlasništvo kupca na novu. Opciono; ostale app-ove ne dira.
   */
  save(companyID: number, order: Order, replacesOrderID?: number) {
    this.logger.debug('OrderService.save', order);

    const url = environment.apiBase + `order/create2`;

    order.items = order.items.filter(i => i.quantity > 0);

    var data = {
      companyID,
      order,
      employee: true,
      replacesOrderID
    }

    return this.http.post<GenericItemApiResponse<Order>>(url, data,
      { headers: { accessDeniedDescription: accessDeniedDescriptions.createOrder } }).pipe(
      map(result => {
        this.logger.debug('OrderService.save result', result);
        if (result.success) {
          return new Order(result.item);
        }

        throw result;
      })
    );
  }

  cancelItems(tableID: number, items?: OrderItemToCancel[]) {
    this.logger.debug('OrderService.cancelItems', items);
    const url = environment.apiBase + `order/CancelOrderItems`;
    var data = {
      tableID: tableID,
      orderItems: items
    }

    return this.http.post<ApiResponse>(url, data,
      { headers: { accessDeniedDescription: accessDeniedDescriptions.cancelOrderItems } }).pipe(
      map(result => {
        this.logger.debug('OrderService.cancelItems result', result);
        if (result.success) {
          return true;
        }

        throw result.errorDescription;
      })
    );
  }

  search(searchParam: SearchOrderOptions) {
    const url = environment.apiBase + `order/get`;

    return this.http.post<GenericListApiResponse<Order>>(url, searchParam,
      { headers: { accessDeniedDescription: accessDeniedDescriptions.getOrders } })
      .pipe(
        map(response => {
          if (response.success) {
            var list: Order[] = [];
            response.items.forEach(i => {
              var no = new Order(i);

              list.push(no);
            });

            return list;
          }
          return null;
        }),
        tap(items => {
          items?.forEach(o => o.created = new Date(o.created));
        })
      );
  }

  /**
   * Kao `search`, ali čita i serverski `total` — osnova za potpuno dovlačenje
   * u serijama (BO lista porudžbina). `total: null` = backend još ne stranči,
   * pa je odgovor možda odsečen na serverski limit.
   * Neuspeh (`success: false`) ovde BACA — pozivalac razlikuje grešku od praznog.
   */
  searchPaged(searchParam: SearchOrderOptions): Observable<{ items: Order[]; total: number | null }> {
    const url = environment.apiBase + `order/get`;

    return this.http.post<PagedListApiResponse<Order>>(url, searchParam,
      { headers: { accessDeniedDescription: accessDeniedDescriptions.getOrders } })
      .pipe(
        map(response => {
          if (!response.success) {
            throw response.errorDescription;
          }
          const items = (response.items ?? []).map(i => {
            const order = new Order(i);
            order.created = new Date(order.created);
            return order;
          });
          return { items, total: response.total ?? null };
        })
      );
  }

  getStatusChangeLog(orderID: number) {
    const url = environment.apiBase + `order/GetStatusChangeLog/${orderID}`;

    return this.http.get<GenericListApiResponse<OrderStatusChangeItem>>(url)
      .pipe(
        map(response => {
          if (response.success) {
            return response.items;
          }
          throw response;
        })
      );
  }


  getActiveInstantOrders(companyID: number) {

    var searchParam: SearchOrderOptions = {
      companyID: companyID,
      status: [OrderStatus.Accepted, OrderStatus.New, OrderStatus.InProduction, OrderStatus.Ready, OrderStatus.NewPreorder, OrderStatus.Pending, OrderStatus.Invalid],
      // EatIn included: kiosk orders (EatIn/Pickup) are direct orders too
      deliveryType: [DeliveryType.Pickup, DeliveryType.Delivery, DeliveryType.EatIn]
    };

    return this.search(searchParam);
  }

  getOrder(orderID: number) {

    const url = environment.apiBase + `order/GetOrder?orderID=${orderID}`;

    return this.http.get<GenericItemApiResponse<Order>>(url).pipe(
      map(
        data => {
          if (data.success) {
            return new Order(data.item);
          }

          throw data.errorDescription;
        }
      )
    );
  }

  customerOrderHistory(companyID: number, customerID?: number) {

    const url = environment.apiBase + `order/customerOrderHistory?companyID=${companyID}&customerID=${customerID}`;

    return this.http.get<GenericListApiResponse<PlacedOrder>>(url).pipe(
      map(
        data => {
          if (data.success) {
            return data.items;
          }

          return [];
        }
      )
    );
  }


  getOrderPDF(orderID: number) {
    const url = environment.apiBase + `ordersPDF/order/${orderID}`;

    const options = { responseType: 'blob' as 'json' };

    return this.http.get<Blob>(url, options).pipe(
      map(
        (res) => {
          return new Blob([res], { type: 'application/pdf' });
        }),
      catchError(e => {
        throw e;
      })
    );

  }



  getOrderListPDF(param: SearchOrderOptions) {
    const url = environment.apiBase + `ordersPDF/orderlist/`;

    const options = { responseType: 'blob' as 'json' };

    return this.http.post<Blob>(url, param, options).pipe(
      map(
        (res) => {
          return new Blob([res], { type: 'application/pdf' });
        }),
      catchError(e => {
        throw e;
      })
    );

  }

  getProductListPDF(param: SearchOrderOptions) {
    const url = environment.apiBase + `ordersPDF/ProductList/`;

    const options = { responseType: 'blob' as 'json' };

    return this.http.post<Blob>(url, param, options).pipe(
      map(
        (res) => {
          return new Blob([res], { type: 'application/pdf' });
        }),
      catchError(e => {
        throw e;
      })
    );

  }
}
